import { Blocs } from '@business/blocs';
import { UserRole } from '@business/entities/account';
import { ContactActivityRequest, ContactId } from '@business/entities/contact';
import { OrgId } from '@business/entities/organization';
import { TimeLogFilterType } from '@business/entities/time_log';
import { BaseBloc } from '@core/utils/bloc';
import { resolve } from '@core/utils/ioc';
import { LoadingState } from '@core/utils/repository/loading_state';
import { forkJoin } from 'rxjs';
import { switchMap, tap } from 'rxjs/operators';

export class OrganizationPageBloc extends BaseBloc {
  private readonly organizationBloc = resolve(Blocs.organization);
  private readonly projectBloc = resolve(Blocs.project);
  private readonly contactBloc = resolve(Blocs.contact);
  private readonly activityBloc = resolve(Blocs.contactActivity);
  private readonly timeLogBloc = resolve(Blocs.timeLog);
  private readonly termsBloc = resolve(Blocs.orderTerm);
  private readonly tierBloc = resolve(Blocs.pricingTeir);
  private readonly tagBloc = resolve(Blocs.tag);
  private readonly userBloc = resolve(Blocs.user);

  constructor(private readonly id: OrgId) {
    super();
  }

  loading = new LoadingState();
  fullLoading = new LoadingState();
  activityLoading = new LoadingState();

  readonly organization$ = this.organizationBloc.selectOrganization(this.id);
  readonly activities$ = this.activityBloc.activities$;
  readonly tags$ = this.tagBloc.tags$;
  readonly openTimers$ = this.timeLogBloc.orgOpenTimers(this.id);
  terms$ = this.termsBloc.terms$;
  tiers$ = this.tierBloc.tiers$;
  saleReps$ = this.userBloc.onlySaleReps$;

  onInit() {
    return forkJoin([
      this.organizationBloc.fetchOrganizationDetails(this.id, true),
      this.timeLogBloc.fetchOpenTimeLogs(TimeLogFilterType.org, this.id),
      this.termsBloc.fetchTerms(),
      this.tierBloc.fetchTiers(),
      this.tagBloc.fetchTags(),
      this.userBloc.fetchUsers(false, 8),
    ]).pipe(
      tap(() => this.loadFullOrganization()),
      this.loading.run(),
    );
  }

  loadFullOrganization = () => {
    return this.organizationBloc.fetchOrganizationDetails(this.id).pipe(this.fullLoading.run()).subscribe();
  };

  onReload() {
    this.organizationBloc.fetchOrganizationDetails(this.id).subscribe();
  }

  syncOrganization = this.organizationBloc.syncOrganization;
  deleteOrganization = this.organizationBloc.deleteOrganization;
  deleteContact = (id: ContactId) =>
    this.contactBloc.deleteContact(id).pipe(switchMap(() => this.organizationBloc.fetchOrganizationDetails(this.id)));

  updateWebsites = this.organizationBloc.updateWebsites;

  deleteAddress = this.organizationBloc.deleteAddress;
  makeAddressPrimary = this.organizationBloc.makeAddressPrimary;
  deletePaymentMethod = this.organizationBloc.deletePaymentMethod;

  deleteProject = this.projectBloc.deleteProject;

  sendInvoice = this.projectBloc.sendInvoice;
  processPayment = this.projectBloc.processPayment;
  manualPayment = this.projectBloc.manualPayment;

  cancelSubscription = this.organizationBloc.cancelSubscription;
  syncSubscription = this.organizationBloc.syncSubscription;
  manualSupportHours = this.organizationBloc.manualSupportHours;
  syncPayments = this.organizationBloc.syncPayments;
  updateBoard = this.projectBloc.updateBoard;

  changePaymentMethod = this.organizationBloc.changePaymentMethod;

  createActivity = (request: ContactActivityRequest) => this.activityBloc.createActivity(request).pipe(this.activityLoading.run());
  updateActivity = this.activityBloc.updateActivity;
  deleteActivity = this.activityBloc.deleteActivity;

  resendLastInvoice = this.organizationBloc.resendLastInvoice;

  sendInvoiceOrg = this.organizationBloc.sendInvoice;
  downloadInvoice = this.organizationBloc.downloadInvoice;
  syncInvoice = this.organizationBloc.syncInvoice;
  voidInvoiceOrg = this.organizationBloc.voidInvoice;
  processPaymentOrg = this.organizationBloc.processPayment;

  detachTag = this.organizationBloc.detachTag;
  attachTags = this.organizationBloc.attachTags;

  detachTagContact = this.organizationBloc.detachTagContact;
  attachTagsContact = this.organizationBloc.attachTagsContact;

  deleteSubAccount = this.organizationBloc.deleteSubAccount;
  makeDefault = this.organizationBloc.makeDefault;
  showOnBoard = this.organizationBloc.showOnBoard;

  updateSubPaymentMethod = this.organizationBloc.updateSubPaymentMethod;

  updateProjectNote = this.projectBloc.updateProjectNote;
  updateSaleReps = this.organizationBloc.updateSaleReps;
}
